import React, { useMemo, useState } from "react";
import { Grid, TextField, Typography, Paper, Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  useGetLeadsAnalyticsQuery,
  useGetUsersLeadsAnalyticsQuery,
} from "services/analytics";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CampaignIcon from "@mui/icons-material/Campaign";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AnalyticsUsersTable from "../AnalyticsUsersTable";

dayjs.extend(relativeTime);

const LEADS_META = {
  addToList: {
    label: "Added to List",
    icon: PlaylistAddIcon,
    color: "#1976d2",
  },
  addToCampaign: {
    label: "Added to Campaign",
    icon: CampaignIcon,
    color: "#2e7d32",
  },
  totalExport: {
    label: "Total Exports",
    icon: FileDownloadIcon,
    color: "#ed6c02",
  },
  leadsCreditsUsed: {
    label: "Credits Used",
    icon: CreditScoreIcon,
    color: "#6d6d6d",
  },
};

const LeadsAnalytics = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);

  const queryParams = useMemo(
    () => ({
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    }),
    [startDate, endDate],
  );

  const { data, isError, isLoading } = useGetLeadsAnalyticsQuery();

  const {
    data: usersLeads = [],
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useGetUsersLeadsAnalyticsQuery({
    ...queryParams,
    page,
    limit: 10,
  });

  const { metrics = {} } = data || {};
  const refreshedText = data?.generatedAt
    ? `Refreshed ${dayjs(data.generatedAt).fromNow()}`
    : null;

  const buildLeadsAnalyticsCards = (summaryData = {}) =>
    Object.entries(summaryData).map(([key, value]) => {
      const meta = LEADS_META[key] || {
        label: key.replace(/([A-Z])/g, " $1"),
        icon: CampaignIcon,
        color: "#9e9e9e",
      };
      return {
        key,
        label: meta.label,
        value: value ?? 0,
        icon: meta.icon,
        color: meta.color,
      };
    });

  const analyticsCards = buildLeadsAnalyticsCards(metrics);

  if (isLoading) return <Typography>Loading analytics...</Typography>;
  if (isError)
    return <Typography color="error">Failed to load analytics</Typography>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end">
          {refreshedText && (
            <Typography variant="caption" color="text.secondary">
              {refreshedText}
            </Typography>
          )}
        </Box>
      </Grid>

      {/* Analytics Cards */}
      {analyticsCards.map((item) => {
        const Icon = item.icon;
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderLeft: `5px solid ${item.color}`,
              }}
            >
              <Box sx={{ color: item.color }}>
                <Icon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h6">{item.value}</Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}

      {/* Date Filters */}
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
            sx={{ ml: 2 }}
          />
        </LocalizationProvider>
      </Grid>

      {/* Users Analytics Table */}
      <Grid item xs={12}>
        <AnalyticsUsersTable
          columns={[
            {
              key: "name",
              label: "User",
              render: (row) => `${row.name.first} ${row.name.last}`,
            },
            { key: "email", label: "Email" },
            {
              key: "addedToList",
              label: "Added to List",
              align: "center",
              render: (row) => row.leads.addedToList,
            },
            {
              key: "addedToCampaign",
              label: "Added to Campaign",
              align: "center",
              render: (row) => row.leads.addedToCampaign,
            },
            {
              key: "totalExports",
              label: "Total Exports",
              align: "center",
              render: (row) => row.leads.totalExports,
            },
            {
              key: "creditsUsed",
              label: "Credits Used",
              align: "center",
              render: (row) => row.leads.creditsUsed,
            },
          ]}
          rows={usersLeads?.data || []}
          loading={isUsersLoading}
          error={isUsersError}
          pagination={usersLeads?.pagination}
          onPageChange={setPage}
        />
      </Grid>
    </Grid>
  );
};

export default LeadsAnalytics;
